import classNames from "classnames";
import _, { debounce, sortBy } from "lodash";
import { JSX, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { searchColleges } from "rivals/redux/colleges/actions";
import { cleanColleges } from "rivals/redux/colleges/reducer";
import { getColleges, getSearching } from "rivals/redux/colleges/selectors";
import { TYPEAHEAD_DEBOUNCE_MILLISECONDS } from "rivals/shared/constants";
import { College } from "rivals/shared/interfaces/College";
import { useAppDispatch } from "rivals/shared/utils/hooks";
import styles from "./index.module.scss";
import strings from "./strings";
import { OwnProps } from "./types";
import Typeahead from "../Typeahead";
import { TypeaheadMatchData } from "../Typeahead/types";

const { inputPlaceholder } = strings;

const getValue = (college: College): string => college.commonName;
const RenderCollegeLabel = ({
  afterMatch,
  beforeMatch,
  match
}: TypeaheadMatchData): JSX.Element => (
  <div>
    <span>{beforeMatch}</span>
    <b>{match}</b>
    <span>{afterMatch}</span>
  </div>
);

const alphabeticalOrderedColleges = (colleges: College[]): College[] => {
  return colleges ? sortBy(colleges, ["commonName"]) : [];
};

const CollegeTypeahead = ({
  alphabetizeResults = true,
  allowClear = false, // Show clear button
  clearSearchResult = false, // Show no value in the Typeahead field if set to true
  inputClassName = "",
  filter = Boolean,
  onClear = (): void => {},
  philter = "",
  placeholder = inputPlaceholder,
  wrapperClassName = "",
  dropdownClassName = "",
  filterStyles = false,
  minTypeahead,
  ...props
}: OwnProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const searching = useSelector(getSearching);
  const collegeResults = useSelector(getColleges) || [];

  const colleges = (alphabetizeResults
    ? alphabeticalOrderedColleges(collegeResults)
    : collegeResults
  ).filter(filter);

  useEffect(() => {
    return (): void => {
      if (clearSearchResult) {
        dispatch(cleanColleges());
      }
    };
  }, [clearSearchResult]);

  const debounceFn = useCallback(
    debounce(
      (value: string) => {
        const isMinValue = minTypeahead ? value?.length >= minTypeahead : true;
        if (isMinValue) {
          dispatch(searchColleges(value));
        } else {
          dispatch(cleanColleges());
        }
      },
      TYPEAHEAD_DEBOUNCE_MILLISECONDS,
      {
        leading: true
      }
    ),
    []
  );

  const handleOnChange = (value: string, option: { item?: College }): void => {
    if (props.onCollegeChange) {
      props.onCollegeChange(value);
    }
    if (option) {
      const { item } = option;

      // When college is required
      if (props.onCollegeChange && props.required && !item) {
        props.onCollegeChange(value);
      }
      if (value && !item) {
        debounceFn(value);
      }
      if (value === "") {
        onClear();
      }
    }
  };

  const handleOnSelect = (college: College): void => {
    if (props.onSelected) {
      props.onSelected(college);
    }
  };

  const inputClassNames = classNames(styles.collegeSearch, inputClassName, {
    [styles.feedback]: props.errors,
    [styles.filter]: filterStyles
  });

  return (
    <Typeahead<College>
      RenderLabel={RenderCollegeLabel}
      allowClear={allowClear}
      defaultValue={props.defaultValue}
      dropdownClassName={dropdownClassName}
      getValue={getValue}
      inputClassNames={inputClassNames}
      loading={searching}
      onChange={handleOnChange}
      onClear={onClear}
      onSelect={handleOnSelect}
      philter={philter}
      placeholder={placeholder}
      searchResults={colleges}
      value={props.value}
      wrapperClassName={classNames(
        styles.collegeTypeaheadWrapper,
        wrapperClassName
      )}
      {..._.omit(props, ["onSelected", "onCollegeChange"])}
    />
  );
};

export default CollegeTypeahead;
